import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import CommonButton from '../../common/components/CommonButton';
import { Timezones } from '../../common/const';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { setLoading } from '../../features/authSlice';
import { updateMentorAvailability } from '../../api/mentorApi';

const MentorAvailability = ({
  editPersonalProfile,
  mentorAvailability,
  timeZone,
  getCurrentUser,
  mentorProfileId
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  const [availability, setAvailability] = useState([]);

  const [selectedTimezone, setSelectedTimezone] = useState(null);

  const days = [
    { id: 'sunday', label: 'SUN' },
    { id: 'monday', label: 'MON' },
    { id: 'tuesday', label: 'TUE' },
    { id: 'wednesday', label: 'WED' },
    { id: 'thursday', label: 'THU' },
    { id: 'friday', label: 'FRI' },
    { id: 'saturday', label: 'SAT' },
  ];

  const getDaySlot = (dayId) => {
    return availability.find(slot => slot.day === dayId);
  };

  const handleDayToggle = (dayId) => {
    const existingSlot = getDaySlot(dayId);
    if (existingSlot) {
      setAvailability(availability.filter(slot => slot.day !== dayId));
    } else {
      setAvailability([...availability, { day: dayId, from: "09:00:00", to: "17:00:00" }]);
    }
  };

  const handleTimeChange = (dayId, field, value) => {
    setAvailability(availability.map(slot =>
      slot.day === dayId ? { ...slot, [field]: value } : slot
    ));
  };


  useEffect(() => {
    setAvailability(mentorAvailability || [
      { day: "monday", from: "09:00", to: "17:00" },
      { day: "tuesday", from: "09:00", to: "17:00" },
      { day: "wednesday", from: "09:00", to: "17:00" },
      { day: "thursday", from: "09:00", to: "17:00" },
      { day: "friday", from: "09:00", to: "17:00" },
    ]);
    setSelectedTimezone(timeZone);
  }, [timeZone]);

  return (
    <Box sx={{ margin: 'auto', width: '100%' }}>
      {loading && (
        <LoadingSpinner />
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
          Weekly Hours
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 350 }}>
            <Select
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              disabled={!editPersonalProfile}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              {Timezones.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CommonButton
            label="Save Changes"
            marginBottom="0px"
            marginTop="0px"
            fontWeight={600}
            width="140px"
            disabled={!editPersonalProfile}
            handleClick={async () => {
              try {
                if (selectedTimezone) {
                  dispatch(setLoading({
                    loading: true
                  }));
                  const response = await updateMentorAvailability(mentorProfileId, {
                    timezone: selectedTimezone,
                    availability: availability
                  });
                  if (response.success) {
                    showNotification(response.message, 'success');
                    getCurrentUser();
                    dispatch(setLoading({
                      loading: false
                    }));
                  }
                } else {
                  showNotification('Please select timezone', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                showNotification(errorMessage, 'error');
                dispatch(setLoading({
                  loading: false
                }));
              }
            }}
          />
        </Box>
      </Box>

      {/* Days with checkboxes and time pickers */}
      {days.map((day) => {
        const daySlot = getDaySlot(day.id);
        return (
          <Box key={day.id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Checkbox */}
              <Box sx={{ width: 70 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!daySlot}
                      disabled={!editPersonalProfile}
                      onChange={() => handleDayToggle(day.id)}
                      sx={{
                        color: '#757575',
                        '&.Mui-checked': { color: '#1976d2' },
                        p: 0.5,
                      }}
                    />
                  }
                  label={day.label}
                  sx={{ margin: 0 }}
                />
              </Box>

              {/* Native HTML5 Time Pickers - Hours and Minutes only */}
              {daySlot ? (
                <>
                  <TextField
                    type="time"
                    value={daySlot.from}
                    onChange={(e) => handleTimeChange(day.id, 'from', e.target.value)}
                    disabled={!editPersonalProfile}
                    size="small"
                    sx={{
                      width: 140,
                      ml: 1,
                      '& .MuiInputBase-input': { fontSize: '0.95rem' },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          border: '1px solid lightgray',
                        },
                        '&.Mui-focused fieldset': {
                          border: '1px solid lightgray',
                        }
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      step: 60, // Show only hours and minutes (no seconds)
                      style: { fontSize: '0.95rem' }
                    }}
                  />
                  <TextField
                    type="time"
                    value={daySlot.to}
                    onChange={(e) => handleTimeChange(day.id, 'to', e.target.value)}
                    disabled={!editPersonalProfile}
                    size="small"
                    sx={{
                      width: 140,
                      ml: 2,
                      '& .MuiInputBase-input': { fontSize: '0.95rem' },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          border: '1px solid lightgray',
                        },
                        '&.Mui-focused fieldset': {
                          border: '1px solid lightgray',
                        }
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      step: 60, // Show only hours and minutes (no seconds)
                      style: { fontSize: '0.95rem' }
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ width: 140, ml: 1 }}></Box>
                  <Box sx={{ width: 140, ml: 2 }}></Box>
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MentorAvailability;